import { type SubmitFunction } from '@remix-run/react'

import { type UseFormResetField } from 'react-hook-form'
import { END, GO_TO_NEXT_JSON_TREE } from '#app/constants/forms.ts'

import { checkIfCurrentStepHasChildren } from '#app/features/form/wizard/utils/checkIfCurrentStepHasChildren.ts'
import {
	getCurrentQuestionFromJSONState,
	getNextQuestionIdAndAnswer,
} from '#app/features/form/wizard/utils/getFromJSONTree.ts'
import { getUserHasUpdatedJSONForm } from '#app/features/form/wizard/utils/getUserHasUpdatedJSONForm.ts'
import { handleSubmitForm } from '#app/features/form/wizard/utils/handleSubmitForm.ts'
import { setJSONValuesToFormState } from '#app/features/form/wizard/utils/setJSONValuesToFormState.ts'
import { setNextQuestionDispatcher } from '#app/features/form/wizard/utils/setNextQuestionDispatcher.ts'
import { type JSONState } from '#types/form/wizard.ts'
import { getUpdateAnsweredQuestionsStack } from './getUpdatedAnsweredQuestionsStack.ts'
import { handleStepLogic } from './handleStepLogic.ts'
import { removeChildrenTrees } from './removeChildrenTrees.ts'

interface CurrentAnswer {
	[key: string]: string
}

interface HandleJSONStateParams<T> {
	submit: SubmitFunction
	resetField: UseFormResetField<any>
	currentFormContext: CurrentAnswer
	CSRFToken: string
	actionRoute: string
	currentStepProps: any
	entireGlobalState: JSONState<T>
}

export function HandleJSONState<T>({
	submit,
	resetField,
	currentFormContext,
	CSRFToken,
	actionRoute,
	currentStepProps,
	entireGlobalState,
}: HandleJSONStateParams<T>) {
	const {
		id,
		fieldKey,
		isInJsonTree,
		questionText,
		hasBackendCall,
		isLastStep,
		backendAction,
	} = currentStepProps

	const {
		oldFormState,
		formState,
		wizardMessages,
		answeredQuestionsStack,
		currentQuestionTreeId,
		questionTree,
		currentQuestionId,
		dispatch,
		type,
	} = entireGlobalState

	if (!isInJsonTree) {
		if (!isLastStep && !hasBackendCall) {
			setNextQuestionDispatcher({
				type,
				isInJsonTree,
				currentQuestionId,
				currentQuestionTreeId,
				currentFormContext,
				fieldKey,
				isLastStep,
				dispatch: dispatch as any,
			})
		}

		if (hasBackendCall && !isLastStep) {
			handleStepLogic({
				questionTree,
				formState,
				oldFormState,
				dispatch: dispatch as any,
				submit,
				hasBackendCall,
				resetField,
				CSRFToken,
				fieldKey,
				answeredQuestionsStack,
				backendAction,
				id,
				actionRoute,
				questionText,
				currentFormContext,
				entireGlobalState: entireGlobalState as any,
				wizardMessages,
			})
		}

		if (isLastStep) {
			handleSubmitForm({
				id,
				questionText,
				fieldKey,
				currentFormContext,
				backendAction,
				actionRoute,
				CSRFToken,
				wizardMessages,
				entireGlobalState,
				submit,
			})
		}

		return
	}

	const currentQuestion = getCurrentQuestionFromJSONState<T>({
		currentQuestionId,
		currentQuestionTreeId,
		questionTree,
	})

	const { nextQuestionId } = getNextQuestionIdAndAnswer({
		currentFormContext,
		currentQuestion,
		fieldKey,
	})

	setNextQuestionDispatcher({
		type,
		isInJsonTree,
		currentQuestionId,
		currentFormContext,
		fieldKey,
		currentQuestionTreeId,
		nextQuestionId,
		isLastStep,
		dispatch: dispatch as any,
	})

	if (nextQuestionId === END) {
		const { userHasNotUpdatedCurrentJSONTree } = getUserHasUpdatedJSONForm({
			answeredQuestionsStack,
			type,
			id,
			currentQuestionId,
			currentQuestionTreeId,
			typeOfQuestion: currentQuestion.type,
			formState,
			oldFormState,
		})

		if (userHasNotUpdatedCurrentJSONTree) {
			dispatch!({ type: GO_TO_NEXT_JSON_TREE })
		} else {
			const { hasChildren, whichChildren } = checkIfCurrentStepHasChildren({
				id: id,
				questionTree,
			})

			if (hasChildren) {
				const [
					filteredTrees,
					newFormState,
					filteredMessages,
					filteredAnsweredQuestionsStack,
				] = removeChildrenTrees({
					questionTree,
					formState,
					wizardMessages,
					whichChildren: whichChildren!,
					currentId: id,
					answeredQuestionsStack,
					resetField,
				})

				const updatedAnsweredQuestionsStack = getUpdateAnsweredQuestionsStack({
					answeredQuestionsStack: filteredAnsweredQuestionsStack,
					currentQuestionId,
					currentQuestionTreeId,
					type,
					id,
				})

				const newState = {
					...entireGlobalState,
					questionTree: filteredTrees,
					answeredQuestionsStack: updatedAnsweredQuestionsStack,
					formState: newFormState,
					wizardMessages: filteredMessages,
				}

				setJSONValuesToFormState({
					questionText,
					fieldKey,
					currentFormContext,
					id,
					CSRFToken,
					actionRoute,
					backendAction,
					questionTree: filteredTrees,
					currentQuestionId,
					currentQuestionTreeId,
					answeredQuestionsStack: filteredAnsweredQuestionsStack,
					type,
					entireGlobalState: newState as any,
					wizardMessages: filteredMessages,
					submit,
				})
			} else {
				setJSONValuesToFormState({
					questionText,
					fieldKey,
					id,
					CSRFToken,
					actionRoute,
					backendAction,
					currentFormContext,
					questionTree,
					currentQuestionId,
					currentQuestionTreeId,
					answeredQuestionsStack,
					type,
					entireGlobalState: entireGlobalState as any,
					wizardMessages,
					submit,
				})
			}
		}
	}
}
